import { InputHTMLAttributes, useCallback, useRef } from 'react'

import { FIELD_INPUT_BASE_CLASS } from './fieldInputStyles'

type NativeDateInputProps = Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> & {
  type?: 'date' | 'datetime-local'
}

/** Opens the native picker on first click on the input; second click allows keyboard entry. */
export default function NativeDateInput({
  className,
  onPointerDown,
  onBlur,
  onKeyDown,
  type = 'date',
  ...props
}: NativeDateInputProps) {
  const inputRef = useRef<HTMLInputElement>(null)
  const pickerEngagedRef = useRef(false)

  const handlePointerDown = useCallback(
    (e: React.PointerEvent<HTMLInputElement>) => {
      onPointerDown?.(e)
      if (e.defaultPrevented || e.button !== 0) return

      const input = inputRef.current
      if (!input || e.target !== input) return

      // Second click while focused: keep picker closed and allow manual typing.
      if (document.activeElement === input && pickerEngagedRef.current) {
        pickerEngagedRef.current = false
        return
      }

      pickerEngagedRef.current = true
      requestAnimationFrame(() => {
        if (!pickerEngagedRef.current) return
        try {
          input.showPicker()
        } catch {
          input.focus()
        }
      })
    },
    [onPointerDown]
  )

  const handleBlur = useCallback(
    (e: React.FocusEvent<HTMLInputElement>) => {
      pickerEngagedRef.current = false
      onBlur?.(e)
    },
    [onBlur]
  )

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      pickerEngagedRef.current = false
      onKeyDown?.(e)
    },
    [onKeyDown]
  )

  return (
    <input
      ref={inputRef}
      type={type}
      {...props}
      onPointerDown={handlePointerDown}
      onBlur={handleBlur}
      onKeyDown={handleKeyDown}
      className={
        className ?? `${FIELD_INPUT_BASE_CLASS} date-input-native cursor-text`
      }
    />
  )
}
